import React, { useState, useRef } from 'react';
import '../styles/home.css';
import { PDFDocument } from 'pdf-lib';
import insertImageIcon from '../assets/insert-image-icon.svg';
import * as pdfjsLib from 'pdfjs-dist';
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.entry';
import Modal from '@cloudscape-design/components/modal';
import Box from '@cloudscape-design/components/box';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Button from '@cloudscape-design/components/button';
import FormField from '@cloudscape-design/components/form-field';
import Spinner from '@cloudscape-design/components/spinner';
import { addUsage } from './add-usage.js';
import constants from '../constants.js';

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

const InsertImageToPdf = ({ isNew }) => {
  const [visible, setVisible] = useState(false);
  const [pdfFile, setPdfFile] = useState(null);
  const [imageLibrary, setImageLibrary] = useState([]);
  const [placedImages, setPlacedImages] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [pagePreview, setPagePreview] = useState(null);
  const [submitValue, setSubmitValue] = useState('Download PDF');
  const [notify, setNotify] = useState('');
  const [error, setError] = useState(null);
  const [step, setStep] = useState(1);
  const [pageDimensions, setPageDimensions] = useState({ width: 0, height: 0 });
  const [zoom, setZoom] = useState(100);
  const [resultBlob, setResultBlob] = useState(null);
  const containerRef = useRef(null);
  const pdfInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const [draggingImage, setDraggingImage] = useState(null);
  const [resizing, setResizing] = useState(null);
  const [scale, setScale] = useState(1);
  const pdfDocRef = useRef(null);

  const toggleModal = () => {
    setVisible(!visible);
    resetState();
  };

  const resetState = () => {
    setPdfFile(null);
    setImageLibrary([]);
    setPlacedImages([]);
    setSelectedImage(null);
    setCurrentPage(1);
    setTotalPages(0);
    setPagePreview(null);
    setSubmitValue('Download PDF');
    setNotify('');
    setError(null);
    setStep(1);
    setZoom(100);
    setResultBlob(null);
    if (pdfInputRef.current) pdfInputRef.current.value = null;
    if (imageInputRef.current) imageInputRef.current.value = null;
  };

  const handlePdfChange = async (event) => {
    const file = event.target.files[0];
    if (!file || file.type !== constants.pdfFileType) {
      setNotify('Please select a valid PDF file');
      setError('error');
      return;
    }
    setPdfFile(file);
    setNotify('');
    setError(null);

    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      pdfDocRef.current = pdf;
      setTotalPages(pdf.numPages);
      setStep(2);
      await renderPage(pdf, 1);
    } catch (err) {
      setNotify('Failed to load PDF');
      setError('error');
    }
  };

  const renderPage = async (pdfDoc, pageNum) => {
    const page = await pdfDoc.getPage(pageNum);
    const viewport = page.getViewport({ scale: 1 });
    const containerWidth = 500;
    const newScale = containerWidth / viewport.width;
    setScale(newScale);
    const scaledViewport = page.getViewport({ scale: newScale * (zoom / 100) });
    setPageDimensions({ width: scaledViewport.width, height: scaledViewport.height });

    const canvas = document.createElement('canvas');
    canvas.width = scaledViewport.width;
    canvas.height = scaledViewport.height;
    await page.render({ canvasContext: canvas.getContext('2d'), viewport: scaledViewport }).promise;
    setPagePreview(canvas.toDataURL());
  };

  const handlePageChange = async (newPage) => {
    if (newPage < 1 || newPage > totalPages || !pdfDocRef.current) return;
    setCurrentPage(newPage);
    await renderPage(pdfDocRef.current, newPage);
  };

  const handleZoom = async (delta) => {
    const newZoom = Math.max(50, Math.min(200, zoom + delta));
    setZoom(newZoom);
    if (pdfDocRef.current) await renderPage(pdfDocRef.current, currentPage);
  };

  const handleImageUpload = (event) => {
    const files = Array.from(event.target.files);
    files.forEach((file) => {
      if (!file.type.match(/image\/(png|jpeg|jpg|webp|gif)/i)) return;
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          setImageLibrary((prev) => [
            ...prev,
            {
              id: Date.now() + Math.random(),
              src: e.target.result,
              name: file.name,
              file,
              width: img.width,
              height: img.height,
            },
          ]);
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
    if (imageInputRef.current) imageInputRef.current.value = null;
  };

  const addImageToPdf = (image) => {
    const maxSize = 150;
    const ratio = Math.min(maxSize / image.width, maxSize / image.height, 1);
    const newImage = {
      id: Date.now() + Math.random(),
      imageId: image.id,
      src: image.src,
      name: image.name,
      file: image.file,
      x: pageDimensions.width / 2 - (image.width * ratio) / 2,
      y: pageDimensions.height / 2 - (image.height * ratio) / 2,
      width: Math.round(image.width * ratio),
      height: Math.round(image.height * ratio),
      opacity: 100,
      page: currentPage,
    };
    setPlacedImages((prev) => [...prev, newImage]);
    setSelectedImage(newImage.id);
    setStep(3);
  };

  const removeFromLibrary = (id) => {
    setImageLibrary((prev) => prev.filter((i) => i.id !== id));
    setPlacedImages((prev) => prev.filter((i) => i.imageId !== id));
  };

  const removePlacedImage = (id) => {
    setPlacedImages((prev) => prev.filter((i) => i.id !== id));
    if (selectedImage === id) setSelectedImage(null);
  };

  const updateImage = (id, updates) => {
    setPlacedImages((prev) => prev.map((i) => (i.id === id ? { ...i, ...updates } : i)));
  };

  const handleMouseDown = (e, image) => {
    e.preventDefault();
    setSelectedImage(image.id);
    const rect = containerRef.current.getBoundingClientRect();
    setDraggingImage({ ...image, offsetX: e.clientX - rect.left - image.x, offsetY: e.clientY - rect.top - image.y });
  };

  const handleResizeMouseDown = (e, image) => {
    e.stopPropagation();
    e.preventDefault();
    setSelectedImage(image.id);
    setResizing({
      id: image.id,
      startMouseX: e.clientX,
      startMouseY: e.clientY,
      startWidth: image.width,
      startHeight: image.height,
      startX: image.x,
      startY: image.y,
    });
  };

  const handleMouseMove = (e) => {
    if (!containerRef.current) return;

    if (resizing) {
      const minSize = 20;
      const ratio = resizing.startHeight / resizing.startWidth;
      const deltaX = e.clientX - resizing.startMouseX;

      // Derive width from the horizontal mouse delta, keep aspect ratio for height.
      let width = resizing.startWidth + deltaX;
      width = Math.max(minSize, width);
      // Clamp so the image stays within the page bounds.
      width = Math.min(width, pageDimensions.width - resizing.startX);
      let height = width * ratio;
      if (height > pageDimensions.height - resizing.startY) {
        height = pageDimensions.height - resizing.startY;
        width = ratio ? height / ratio : width;
      }
      height = Math.max(minSize, height);
      width = Math.max(minSize, width);

      const finalWidth = Math.round(width);
      const finalHeight = Math.round(height);
      setPlacedImages((prev) =>
        prev.map((i) => (i.id === resizing.id ? { ...i, width: finalWidth, height: finalHeight } : i)),
      );
      return;
    }

    if (!draggingImage) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = Math.max(
      0,
      Math.min(e.clientX - rect.left - draggingImage.offsetX, pageDimensions.width - draggingImage.width),
    );
    const y = Math.max(
      0,
      Math.min(e.clientY - rect.top - draggingImage.offsetY, pageDimensions.height - draggingImage.height),
    );
    setPlacedImages((prev) => prev.map((i) => (i.id === draggingImage.id ? { ...i, x, y } : i)));
  };

  const handleMouseUp = () => {
    setDraggingImage(null);
    setResizing(null);
  };

  const applyImages = async () => {
    if (!pdfFile || placedImages.length === 0) return;
    setSubmitValue(<Spinner />);

    try {
      const pdfBytes = await pdfFile.arrayBuffer();
      const pdfDoc = await PDFDocument.load(pdfBytes);
      const pages = pdfDoc.getPages();

      // Helper to convert base64 to Uint8Array
      const base64ToUint8Array = (base64) => {
        const base64Data = base64.split(',')[1];
        const binaryString = atob(base64Data);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        return bytes;
      };

      for (const img of placedImages) {
        const imageBytes = base64ToUint8Array(img.src);
        const image = img.file.type.includes('png')
          ? await pdfDoc.embedPng(imageBytes)
          : await pdfDoc.embedJpg(imageBytes);

        const page = pages[img.page - 1];
        if (!page) continue;
        const { height: pageHeight } = page.getSize();
        const zoomFactor = zoom / 100;
        const pdfX = img.x / (scale * zoomFactor);
        const pdfY = pageHeight - img.y / (scale * zoomFactor) - img.height / (scale * zoomFactor);

        page.drawImage(image, {
          x: pdfX,
          y: pdfY,
          width: img.width / (scale * zoomFactor),
          height: img.height / (scale * zoomFactor),
          opacity: img.opacity / 100,
        });
      }

      const modifiedPdfBytes = await pdfDoc.save();
      const blob = new Blob([modifiedPdfBytes], { type: constants.pdfFileType });
      setResultBlob(blob);
      setSubmitValue('Download PDF');
      addUsage('AWESOME PDF TOOLS', '1.0', 1, window.location.href);
      setStep(4);
    } catch (err) {
      console.error('Error inserting images:', err);
      setNotify('Failed to insert images. Please try again.');
      setError('error');
      setSubmitValue('Download PDF');
    }
  };

  const downloadResult = () => {
    if (!resultBlob) return;
    const url = URL.createObjectURL(resultBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `images_${pdfFile.name}`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const currentPageImages = placedImages.filter((i) => i.page === currentPage);
  const selectedImageData = placedImages.find((i) => i.id === selectedImage);

  return (
    <div>
      <div className="service-card" onClick={toggleModal}>
        {isNew && <span className="badge badge-new">NEW</span>}
        <div className="service-card-icon">
          <img src={insertImageIcon} alt="Insert Images" loading="lazy" />
        </div>
        <h3 className="service-card-title">Insert Images in PDF</h3>
        <p className="service-card-desc">Add unique images to specific individual pages</p>
        <div className="service-card-action">
          <span>Get Started</span>
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z" />
          </svg>
        </div>
      </div>

      <Modal
        onDismiss={toggleModal}
        visible={visible}
        size="max"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={toggleModal}>
                Cancel
              </Button>
              {step === 3 && placedImages.length > 0 && (
                <Button variant="primary" onClick={applyImages}>
                  {submitValue}
                </Button>
              )}
            </SpaceBetween>
          </Box>
        }
        header="INSERT IMAGES IN PDF — Add unique images to specific pages"
      >
        <div>
          {/* Progress Steps */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '16px',
              marginBottom: '20px',
              padding: '12px',
              backgroundColor: '#f5f5f5',
              borderRadius: '8px',
            }}
          >
            {['Upload PDF', 'Add Images', 'Place & Edit', 'Download'].map((label, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div
                  style={{
                    width: '28px',
                    height: '28px',
                    borderRadius: '50%',
                    backgroundColor: step > i + 1 ? '#52c41a' : step === i + 1 ? '#FF9900' : '#ddd',
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '14px',
                    fontWeight: '600',
                  }}
                >
                  {i + 1}
                </div>
                <span
                  style={{
                    fontSize: '13px',
                    fontWeight: step === i + 1 ? '600' : '400',
                    color: step >= i + 1 ? '#333' : '#999',
                  }}
                >
                  {label}
                </span>
                {i < 3 && <span style={{ color: '#ccc', margin: '0 8px' }}>→</span>}
              </div>
            ))}
          </div>

          {step < 4 ? (
            <div style={{ display: 'flex', gap: '20px' }}>
              {/* Left Panel */}
              <div style={{ width: '250px', flexShrink: 0 }}>
                <div className="bordered-box">
                  <h6 className="container-header">📄 Choose PDF File</h6>
                  <FormField errorText={notify}>
                    <input
                      ref={pdfInputRef}
                      type="file"
                      accept=".pdf"
                      onChange={handlePdfChange}
                      style={{ width: '100%' }}
                    />
                  </FormField>
                  {pdfFile && (
                    <div style={{ marginTop: '8px', color: '#52c41a', fontSize: '13px' }}>
                      📄 {pdfFile.name} ({totalPages} pages)
                    </div>
                  )}
                </div>

                {step >= 2 && (
                  <div className="bordered-box">
                    <h6 className="container-header">🖼️ Image Library ({imageLibrary.length})</h6>
                    <label
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '6px 12px',
                        marginBottom: '12px',
                        backgroundColor: '#FF9900',
                        color: '#fff',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '13px',
                      }}
                    >
                      ➕ Add Images
                      <input
                        ref={imageInputRef}
                        type="file"
                        accept=".png,.jpg,.jpeg,.webp,.gif"
                        multiple
                        onChange={handleImageUpload}
                        style={{ display: 'none' }}
                      />
                    </label>
                    <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                      {imageLibrary.map((img) => (
                        <div
                          key={img.id}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '8px',
                            backgroundColor: '#fafafa',
                            borderRadius: '6px',
                            marginBottom: '8px',
                            border: '1px solid #eee',
                          }}
                        >
                          <img
                            src={img.src}
                            alt=""
                            style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '4px' }}
                          />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div
                              style={{
                                fontSize: '11px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {img.name}
                            </div>
                            <div style={{ fontSize: '10px', color: '#999' }}>
                              {img.width}×{img.height}
                            </div>
                            <div style={{ display: 'flex', gap: '4px', marginTop: '4px' }}>
                              <button
                                onClick={() => addImageToPdf(img)}
                                style={{
                                  padding: '2px 6px',
                                  fontSize: '10px',
                                  backgroundColor: '#52c41a',
                                  color: '#fff',
                                  border: 'none',
                                  borderRadius: '3px',
                                  cursor: 'pointer',
                                }}
                              >
                                Insert
                              </button>
                              <button
                                onClick={() => removeFromLibrary(img.id)}
                                style={{
                                  padding: '2px 6px',
                                  fontSize: '10px',
                                  backgroundColor: '#ff4d4f',
                                  color: '#fff',
                                  border: 'none',
                                  borderRadius: '3px',
                                  cursor: 'pointer',
                                }}
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Center - PDF Preview */}
              <div style={{ flex: 1 }}>
                {pagePreview && (
                  <div className="bordered-box">
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '12px',
                        padding: '8px',
                        backgroundColor: '#f5f5f5',
                        borderRadius: '6px',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <button
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage <= 1}
                          style={{
                            padding: '4px 8px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            cursor: currentPage <= 1 ? 'not-allowed' : 'pointer',
                            backgroundColor: '#fff',
                          }}
                        >
                          ◀
                        </button>
                        <span style={{ fontSize: '13px' }}>
                          Page {currentPage} / {totalPages}
                        </span>
                        <button
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage >= totalPages}
                          style={{
                            padding: '4px 8px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            cursor: currentPage >= totalPages ? 'not-allowed' : 'pointer',
                            backgroundColor: '#fff',
                          }}
                        >
                          ▶
                        </button>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <button
                          onClick={() => handleZoom(-10)}
                          style={{
                            padding: '4px 8px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            backgroundColor: '#fff',
                          }}
                        >
                          ➖
                        </button>
                        <span style={{ fontSize: '13px', minWidth: '40px', textAlign: 'center' }}>{zoom}%</span>
                        <button
                          onClick={() => handleZoom(10)}
                          style={{
                            padding: '4px 8px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            backgroundColor: '#fff',
                          }}
                        >
                          ➕
                        </button>
                      </div>
                    </div>

                    <div
                      style={{ overflow: 'auto', maxHeight: '500px', border: '1px solid #ddd', borderRadius: '4px' }}
                    >
                      <div
                        ref={containerRef}
                        onMouseMove={handleMouseMove}
                        onMouseUp={handleMouseUp}
                        onMouseLeave={handleMouseUp}
                        style={{
                          position: 'relative',
                          width: pageDimensions.width,
                          height: pageDimensions.height,
                          margin: '0 auto',
                          backgroundImage: `url(${pagePreview})`,
                          backgroundSize: 'contain',
                          cursor: resizing ? 'nwse-resize' : draggingImage ? 'grabbing' : 'default',
                        }}
                      >
                        {currentPageImages.map((img) => (
                          <div
                            key={img.id}
                            onMouseDown={(e) => handleMouseDown(e, img)}
                            style={{
                              position: 'absolute',
                              left: img.x,
                              top: img.y,
                              width: img.width,
                              height: img.height,
                              cursor: 'grab',
                              border: selectedImage === img.id ? '2px solid #FF9900' : '2px dashed #1890ff',
                              boxSizing: 'border-box',
                              opacity: img.opacity / 100,
                            }}
                          >
                            <img
                              src={img.src}
                              alt=""
                              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                              draggable={false}
                            />
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                removePlacedImage(img.id);
                              }}
                              style={{
                                position: 'absolute',
                                top: -10,
                                right: -10,
                                width: '20px',
                                height: '20px',
                                borderRadius: '50%',
                                backgroundColor: '#ff4d4f',
                                color: '#fff',
                                border: 'none',
                                cursor: 'pointer',
                                fontSize: '14px',
                              }}
                            >
                              ×
                            </button>
                            <div
                              onMouseDown={(e) => handleResizeMouseDown(e, img)}
                              title="Drag to resize"
                              style={{
                                position: 'absolute',
                                bottom: -6,
                                right: -6,
                                width: '12px',
                                height: '12px',
                                backgroundColor: '#FF9900',
                                border: '1px solid #fff',
                                borderRadius: '2px',
                                cursor: 'nwse-resize',
                              }}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Right Panel - Settings */}
              {selectedImageData && (
                <div style={{ width: '200px', flexShrink: 0 }}>
                  <div className="bordered-box">
                    <h6 className="container-header">Image Settings</h6>
                    <div style={{ marginBottom: '12px' }}>
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', marginBottom: '4px' }}>
                        Opacity
                      </label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <input
                          type="range"
                          min="10"
                          max="100"
                          value={selectedImageData.opacity}
                          onChange={(e) => updateImage(selectedImage, { opacity: parseInt(e.target.value) })}
                          style={{ flex: 1 }}
                        />
                        <span style={{ fontSize: '12px', minWidth: '35px' }}>{selectedImageData.opacity}%</span>
                      </div>
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', marginBottom: '4px' }}>
                        Size (px)
                      </label>
                      <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                        <input
                          type="number"
                          value={selectedImageData.width}
                          onChange={(e) => updateImage(selectedImage, { width: parseInt(e.target.value) || 50 })}
                          style={{
                            width: '55px',
                            padding: '4px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            fontSize: '12px',
                          }}
                        />
                        <span>×</span>
                        <input
                          type="number"
                          value={selectedImageData.height}
                          onChange={(e) => updateImage(selectedImage, { height: parseInt(e.target.value) || 50 })}
                          style={{
                            width: '55px',
                            padding: '4px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            fontSize: '12px',
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div
              className="bordered-box"
              style={{ maxWidth: '500px', margin: '0 auto', textAlign: 'center', padding: '40px' }}
            >
              <div style={{ fontSize: '30px', marginBottom: '16px' }}>✅</div>
              <h3 style={{ marginBottom: '8px', color: '#52c41a' }}>Images Added Successfully!</h3>
              <p style={{ color: '#666', marginBottom: '24px' }}>Your PDF with images is ready to download.</p>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', flexWrap: 'wrap' }}>
                <Button variant="primary" onClick={downloadResult}>
                  📥 Download PDF
                </Button>
                <Button variant="normal" onClick={() => setStep(3)}>
                  ✏️ Edit Again
                </Button>
                <Button variant="link" onClick={resetState}>
                  🔄 Start Over
                </Button>
              </div>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default InsertImageToPdf;
